import React, { useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';

import { CommandLineEntry, CommandLineEntryStack } from '../common';
import { useTerminal } from '../TerminalProvider';

const StakingConsoleContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing('2000')};

  overflow-y: auto;
  scroll-behavior: smooth;
  gap: ${({ theme }) => theme.spacing('1000')};
  text-shadow: 0px 0px 4px ${({ theme }) => theme.color.greenFixed};

  ${({ theme }) => theme.breakpoints.down('md')} {
    height: 25rem;
  }
`;

const CountBox = styled.span`
  padding: ${({ theme }) => theme.spacing('0250')};
  background-color: ${({ theme }) => theme.color.greenFixed};
  color: ${({ theme }) => theme.color.onGreenFixed};
  border-radius: ${({ theme }) => theme.shape.corner['0250']};

  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
`;

interface IStakingConsoleProps {
  blockchain: Blockchain | string;
  numberOfUnstaked: string;
  numberOfStaked: string;
  children?: React.ReactNode;
}

export const StakingConsole = ({
  blockchain,
  numberOfUnstaked,
  numberOfStaked,
  children,
}: IStakingConsoleProps) => {
  const t = useTranslations();
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const { consoleHistory } = useTerminal();

  useEffect(() => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop =
        scrollableContainerRef.current.scrollHeight;
    }
  }, [consoleHistory.length]);

  return (
    <StakingConsoleContainer ref={scrollableContainerRef}>
      <CommandLineEntryStack>
        <CommandLineEntry centered whiteSpace="unset">
          {t('STAKING_COPYRIGHT')}
        </CommandLineEntry>
        <CommandLineEntry prefix=">" centered>
          {t('STAKING_TERMINAL_ACTIVE_BLOCKCHAIN', { blockchain })}
        </CommandLineEntry>
        <CommandLineEntry prefix=">" centered>
          {t.rich('STAKING_TERMINAL_UNSTAKED_PERSONAS', {
            count: () => <CountBox>{numberOfUnstaked}</CountBox>,
          })}
        </CommandLineEntry>
        <CommandLineEntry prefix=">" centered>
          {t.rich('STAKING_TERMINAL_STAKED_PERSONAS', {
            count: () => <CountBox>{numberOfStaked}</CountBox>,
          })}
        </CommandLineEntry>
      </CommandLineEntryStack>
      {children}
    </StakingConsoleContainer>
  );
};
